import { TransformMode } from "./TransformMode";

class TransformModeInjectClass implements TransformMode {
    private _elements: HTMLDivElement[] = [];
    private _quad: { points: DOMPoint[] } = {
        points: [
            new DOMPoint(),
            new DOMPoint(),
            new DOMPoint(),
            new DOMPoint(),
        ],
    };
    constructor() {
        for (var i = 0; i < 4; ++i) {
            var div = document.createElement("div");
            div.style.position = "absolute";
            div.style.left = "0";
            div.style.top = "0";
            div.style.transformOrigin = "0 0";
            this._elements.push(div);
        }
    }

    private parse(value: any, defaultValue: number): number {
        value = parseFloat(value);
        if (isNaN(value)) {
            return defaultValue;
        }
        return value;
    }

    public extract(element: HTMLElement): DOMMatrix {
        const style = window.getComputedStyle(element);

        // let borderTop = this.parse(style.borderTopWidth, 0);
        // let borderLeft = this.parse(style.borderLeftWidth, 0);
        // borderLeft -= element.scrollLeft;
        // borderTop -= element.scrollTop;

        const elementWidth = 1000;
        const elementHeight = 1000;

        const left = -this.parse(style.borderLeftWidth, 0)
        const top = -this.parse(style.borderTopWidth, 0);
        const right = left + elementWidth;
        const bottom = top + elementHeight;

        //Move the divs to the outer corners.
        this._elements[0].style.transform =
            "translate(" + left + "px, " + top + "px)";
        this._elements[1].style.transform =
            "translate(" + right + "px, " + top + "px)";
        this._elements[2].style.transform =
            "translate(" + right + "px, " + bottom + "px)";
        this._elements[3].style.transform =
            "translate(" + left + "px, " + bottom + "px)";

        //Inject the divs to the target element.
        if (element.shadowRoot) {
            this.inject(element.shadowRoot);
        } else {
            this.inject(element);
        }

        //Apply the bounding-rect positions to the quad points.
        for (var i = 0; i < 4; ++i) {
            const r = this._elements[i].getBoundingClientRect();
            const pt = this._quad.points[i];
            pt.x = r.left;
            pt.y = r.top;
        }

        //Remove the divs from the target element.
        this.reject();

        const src = [
            new DOMPoint(0, 0),
            new DOMPoint(elementWidth, 0),
            new DOMPoint(elementWidth, elementHeight),
            new DOMPoint(0, elementHeight),
        ];
        const dst = [
            this._quad.points[0],
            this._quad.points[1],
            this._quad.points[2],
            this._quad.points[3],
        ];

        return this.getDOMMatrixFromPoints(src, dst);
    }

    private inject(element: HTMLElement | ShadowRoot): void {
        for (var div of this._elements) {
            element.appendChild(div);
        }
    }

    private reject(): void {
        for (var div of this._elements) {
            if (div.parentElement) div.parentElement.removeChild(div);
        }
    }

    /**
     * Computes a DOMMatrix that maps 4 source DOMPoints to 4 destination DOMPoints.
     */
    private getDOMMatrixFromPoints(
        src: DOMPoint[],
        dst: DOMPoint[]
    ): DOMMatrix {
        const A: number[][] = [];
        const b: number[] = [];

        for (let i = 0; i < 4; i++) {
            const { x: x1, y: y1 } = src[i];
            const { x: x2, y: y2 } = dst[i];

            A.push([x1, y1, 1, 0, 0, 0, -x2 * x1, -x2 * y1]);
            b.push(x2);

            A.push([0, 0, 0, x1, y1, 1, -y2 * x1, -y2 * y1]);
            b.push(y2);
        }

        const h = this.solveLinearSystem(A, b);
        h[8] = 1;

        return new DOMMatrix([
            h[0],
            h[3],
            0,
            h[6],
            h[1],
            h[4],
            0,
            h[7],
            0,
            0,
            1,
            0,
            h[2],
            h[5],
            0,
            h[8],
        ]);
    }

    /**
     * Solves a system of linear equations Ax = b using Gaussian elimination.
     */
    private solveLinearSystem(A: number[][], b: number[]): number[] {
        const n = A.length;
        const M = A.map((row, i) => [...row, b[i]]); // Augment matrix

        for (let i = 0; i < n; i++) {
            // Find pivot
            let maxRow = i;
            for (let j = i + 1; j < n; j++) {
                if (Math.abs(M[j][i]) > Math.abs(M[maxRow][i])) {
                    maxRow = j;
                }
            }
            [M[i], M[maxRow]] = [M[maxRow], M[i]];

            for (let j = i + 1; j < n; j++) {
                const factor = M[j][i] / M[i][i];
                for (let k = i; k <= n; k++) {
                    M[j][k] -= factor * M[i][k];
                }
            }
        }

        const x = new Array(n).fill(0);
        for (let i = n - 1; i >= 0; i--) {
            x[i] = M[i][n] / M[i][i];
            for (let k = i - 1; k >= 0; k--) {
                M[k][n] -= M[k][i] * x[i];
            }
        }

        return x;
    }
}

export const TransformModeInject = new TransformModeInjectClass();
